import is from '@sindresorhus/is';
import type { FunctionComponent, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { COMMENT_BUTTON_ATTRIBUTE } from './anchor/grammar';
import { SelectionCommentButton } from './SelectionCommentButton';

type ContainedSelectionCommentButtonProps = {
    readonly containerRef: RefObject<HTMLElement | null>;
    readonly disabled?: boolean;
    readonly onComment: (rect: DOMRect) => void;
};

const captureSelectionRect = (container: HTMLElement): DOMRect | null => {
    const selection = window.getSelection();
    if (is.null(selection) || selection.isCollapsed || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (
        range.toString().trim().length === 0 ||
        !container.contains(range.startContainer) ||
        !container.contains(range.endContainer)
    ) {
        return null;
    }
    return range.getBoundingClientRect();
};

export const ContainedSelectionCommentButton: FunctionComponent<ContainedSelectionCommentButtonProps> = ({
    containerRef,
    disabled = false,
    onComment,
}) => {
    const [rect, setRect] = useState<DOMRect | null>(null);
    const suppressClearRef = useRef(false);
    const clearSelection = useCallback(() => setRect(null), []);

    useEffect(() => {
        const container = containerRef.current;
        if (is.null(container)) return;

        const handleMouseDown = (event: MouseEvent): void => {
            const target = event.target instanceof HTMLElement ? event.target : null;
            if (!is.null(target) && !is.null(target.closest(`[${COMMENT_BUTTON_ATTRIBUTE}]`))) {
                suppressClearRef.current = true;
                return;
            }
            clearSelection();
        };
        const handleMouseUp = (): void => {
            if (suppressClearRef.current) {
                suppressClearRef.current = false;
                return;
            }
            setRect(captureSelectionRect(container));
        };

        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('scroll', clearSelection, { capture: true, passive: true });
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('scroll', clearSelection, { capture: true });
        };
    }, [clearSelection, containerRef]);

    if (disabled || is.null(rect)) return null;

    return (
        <SelectionCommentButton
            rect={rect}
            onComment={() => {
                onComment(rect);
                window.getSelection()?.removeAllRanges();
                clearSelection();
            }}
        />
    );
};
